/**
 * useClubBranding — fetches the active org's club branding data.
 *
 * Calls GET /api/clubs?limit=1 to retrieve the club associated with the
 * current org, then extracts logoUrl, primaryColor, secondaryColor, and name.
 *
 * Falls back to Kantera's default indigo/emerald palette when colors are null
 * so every page that consumes this hook always receives valid color values.
 */

import { useMemo } from 'react';
import { useApi } from './useApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Shape of one item returned by GET /api/clubs — includes branding fields
 *  that are present in the API response but not in the leaner ApiClubListItem. */
interface ClubBrandingItem {
  id: string;
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
}

interface ClubBrandingEnvelope {
  data: ClubBrandingItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ClubBranding {
  /** Club logo image URL, or null when no logo has been uploaded. */
  logoUrl: string | null;
  /** Primary brand color (hex). Falls back to Kantera indigo when not set. */
  primaryColor: string;
  /** Secondary brand color (hex). Falls back to Kantera emerald when not set. */
  secondaryColor: string;
  /** Club display name. */
  clubName: string;
  /** True while the branding data is being fetched. */
  loading: boolean;
}

// ---------------------------------------------------------------------------
// Default palette — Kantera indigo/emerald from C tokens
// ---------------------------------------------------------------------------

const DEFAULT_PRIMARY = '#818cf8'; // C.accent.indigo
const DEFAULT_SECONDARY = '#34d399'; // C.accent.emerald

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the active org's club branding.
 *
 * Usage:
 *   const { logoUrl, primaryColor, secondaryColor, clubName, loading } = useClubBranding();
 */
export function useClubBranding(): ClubBranding {
  const { data, loading } = useApi<ClubBrandingEnvelope>('/api/clubs?limit=1');

  return useMemo<ClubBranding>(() => {
    const club = data?.data?.[0] ?? null;

    return {
      logoUrl: club?.logoUrl ?? null,
      primaryColor: club?.primaryColor ?? DEFAULT_PRIMARY,
      secondaryColor: club?.secondaryColor ?? DEFAULT_SECONDARY,
      clubName: club?.name ?? 'Kantera',
      loading,
    };
  }, [data, loading]);
}
